import { useQuery } from "@tanstack/react-query";
import {
  fetchOverview,
  fetchSourceStats,
  triggerCrawl,
  type OverviewStats,
  type SourceStat,
} from "../../api";
import SeriesDataPanel from "../../components/SeriesDataPanel";
import SurveyCard from "../../components/SurveyCard";

const SOURCE_TYPE_LABEL: Record<string, string> = {
  official_gov: "官方",
  official_pr: "官方PR",
  news: "资讯",
  social: "社媒",
};

const SOURCE_TYPE_COLORS: Record<string, string> = {
  official_gov: "text-ink-secondary bg-surface-sunken border-border font-semibold",
  official_pr: "text-ink-secondary bg-surface-sunken border-border",
  news: "text-ink-tertiary bg-surface-sunken border-border",
  social: "text-ink-tertiary bg-surface-sunken border-border",
};

export default function StatsPage() {
  const { data: overview } = useQuery<OverviewStats>({ queryKey: ["overview"], queryFn: fetchOverview });
  const { data: sources = [] } = useQuery<SourceStat[]>({ queryKey: ["source-stats"], queryFn: fetchSourceStats });

  const sourcesTyped = sources;

  const handleTriggerCrawl = async (code: string) => {
    await triggerCrawl(code);
    alert(`已触发爬虫: ${code}`);
  };

  return (
    <div className="space-y-5">
      {/* Section header */}
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Data Center</p>
          <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">宏观数据与指标</h1>
              <p className="text-sm leading-6 text-ink-secondary">
                报告系列指标数据、央行问卷调查及数据源概览。
              </p>
            </div>
            {overview && (
              <div className="flex flex-wrap gap-2 text-2xs text-ink-tertiary">
                {[
                  { label: "文章总数", value: overview.total_articles },
                  { label: "已分析", value: overview.total_analyzed },
                  { label: "今日新增", value: overview.today_new },
                  { label: "待分析", value: overview.pending_analysis },
                ].map((card) => (
                  <span key={card.label} className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                    {card.label} {String(card.value ?? "—")}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Two-column layout */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* Left: data panels */}
        <div className="flex-1 min-w-0 space-y-5">
          <section className="space-y-3">
            <div className="flex items-center justify-between px-1">
              <div>
                <p className="text-label">Series Data</p>
                <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">报告系列 · 指标数据</h2>
              </div>
              <span className="text-2xs text-ink-tertiary">点击指标名查看历史走势</span>
            </div>
            <SeriesDataPanel />
          </section>

          <section className="space-y-3">
            <div className="px-1">
              <p className="text-label">Surveys</p>
              <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">央行问卷调查</h2>
            </div>
            <SurveyCard />
          </section>
        </div>

        {/* Right: data sources */}
        <div className="w-full lg:w-72 shrink-0 space-y-3">
          <div className="flex items-center justify-between px-1">
            <div>
              <p className="text-label">Sources</p>
              <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">数据源</h2>
            </div>
            <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-mono tabular-nums text-ink-tertiary">
              {sourcesTyped.length} 个
            </span>
          </div>
          <div className="paper-panel divide-y divide-border overflow-hidden">
            {sourcesTyped.map((source) => (
              <div key={source.code} className="px-4 py-3 flex items-center gap-2 transition-colors hover:bg-surface">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-ink truncate">{source.name}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded border font-medium leading-none tracking-wide ${
                        SOURCE_TYPE_COLORS[source.source_type] ?? "text-ink-tertiary bg-surface-sunken border-border"
                      }`}
                    >
                      {SOURCE_TYPE_LABEL[source.source_type] ?? source.source_type}
                    </span>
                    {!source.is_active && <span className="text-[10px] text-negative font-medium">停用</span>}
                  </div>
                  <div className="flex gap-3 mt-1 text-[10px] text-ink-tertiary font-mono tabular-nums">
                    <span>{source.article_count} 篇</span>
                    {source.last_crawled_at && (
                      <span>{source.last_crawled_at.slice(0, 10)}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleTriggerCrawl(source.code)}
                  className="text-2xs text-primary-500 hover:text-primary-600 px-2.5 py-1.5 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 shrink-0 transition-colors font-medium"
                >
                  触发
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
